import {HiCheck as CheckIcon} from 'react-icons/hi2'
import {useCallback, useMemo} from 'react'
import {compact} from 'lodash-es'
import clsx from 'clsx'
import {ProductSize, ProductVariant} from '~/gql/graphql'

type SizeSelectorProps = {
    label: string
    variants: ProductVariant[]
    selectedAppearanceId: string
    selectedSizeId: string | undefined
    onSizeSelect: (size: ProductSize) => void
}

const SizeSelector = ({
    label,
    variants,
    selectedAppearanceId,
    selectedSizeId,
    onSizeSelect,
}: SizeSelectorProps) => {
    const sizes = useMemo(() => {
        const sizes = variants
            ?.filter((variant) => variant?.appearance?.id === selectedAppearanceId)
            .map((variant) => variant.size)
        return compact(sizes)
    }, [variants, selectedAppearanceId])

    const isAvailableSize = useCallback(
        (sizeId: string) =>
            variants.some(
                (variant) =>
                    variant.appearance?.id === selectedAppearanceId &&
                    variant.size?.id === sizeId &&
                    (variant.stockQuantity ?? 0) > 0
            ),
        [variants, selectedAppearanceId]
    )

    return (
        <fieldset aria-label={label} className="flex flex-row flex-wrap gap-3">
            {sizes.map((size) => {
                const isAvailable = isAvailableSize(size.id!)
                const isSelected = selectedSizeId === size.id

                return (
                    <label className="relative" key={size.id!}>
                        <div
                            className={clsx(
                                'min-w-12 rounded border px-2 py-1 text-center bg-gray-700/10 shadow-lg',
                                !isAvailable && 'crossed opacity-75',
                                isSelected && isAvailable
                                    ? 'border-comet ring-1 ring-comet'
                                    : 'border-white'
                            )}
                        >
                            <input
                                type="radio"
                                name="size-choice"
                                value={size?.id!}
                                checked={isAvailable && isSelected}
                                className="sr-only"
                                disabled={!isAvailable}
                                onChange={() => isAvailable && onSizeSelect(size)}
                            />
                            <span>{size.name}</span>
                        </div>
                        {isSelected && isAvailable && (
                            <div className="absolute -right-2 -top-2 size-5 rounded-full bg-comet">
                                <CheckIcon className="size-full p-0.5" />
                            </div>
                        )}
                    </label>
                )
            })}
        </fieldset>
    )
}

export default SizeSelector
